/* Progress state: what has been read, passed and when it was last reviewed.
 *
 * The artifact kept this in a mutable global `S` and re-rendered by rebuilding
 * innerHTML. Here it is one persisted store, read through useSyncExternalStore
 * so every component re-renders from the same snapshot. The storage key is the
 * artifact's own, so a learner who already worked through the page keeps their
 * progress and their game.
 *
 * Writes are batched into a microtask: answering a question touches `chk`,
 * `rev` and `cur` in the same turn and localStorage should see that once.
 */
import { useSyncExternalStore } from 'react';
import { STORAGE_KEY } from '../data/srs';
import type { Lang, RevRow, SavedGame, SavedState } from '../types';

const EMPTY: SavedState = { done: {}, chk: {}, cur: {}, rev: {} };

export const STATE_VERSION = 3;

let state: SavedState = EMPTY;
let saveFailed = false;
let listeners: Array<() => void> = [];
let errorHandlers: Array<() => void> = [];
let flushQueued = false;

/* Read the artifact's own blob. Language lives with i18next now, but the blob
   may still carry the choice made on the original page. */
function readBlob(): { state: SavedState; lang: Lang | null } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<SavedState> & { lang?: string };
    return {
      lang: parsed.lang === 'fr' || parsed.lang === 'en' ? parsed.lang : null,
      state: {
        done: parsed.done ?? {},
        chk: parsed.chk ?? {},
        cur: parsed.cur ?? {},
        rev: parsed.rev ?? {},
        game: parsed.game ?? null,
        v: parsed.v,
      },
    };
  } catch {
    /* a browser that refuses local storage, or a corrupt entry: start clean
       rather than taking the page down with it. */
    return null;
  }
}

function flush(): void {
  flushQueued = false;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    if (!saveFailed) {
      saveFailed = true;
      for (const h of errorHandlers) h();
    }
  }
}

function commit(next: SavedState): void {
  state = next;
  for (const l of listeners) l();
  if (!flushQueued) {
    flushQueued = true;
    queueMicrotask(flush);
  }
}

/* ── subscription surface ─────────────────────────────────────────── */
export function subscribe(listener: () => void): () => void {
  listeners.push(listener);
  return () => {
    listeners = listeners.filter((l) => l !== listener);
  };
}

export function getState(): SavedState {
  return state;
}

export function useProgress(): SavedState {
  return useSyncExternalStore(subscribe, getState, getState);
}

export function onSaveError(handler: () => void): () => void {
  errorHandlers.push(handler);
  return () => {
    errorHandlers = errorHandlers.filter((h) => h !== handler);
  };
}

/* ── actions ──────────────────────────────────────────────────────── */
export function markDone(id: string): void {
  if (state.done[id]) return;
  commit({ ...state, done: { ...state.done, [id]: true } });
}

export function setChecked(id: string, value: boolean): void {
  commit({ ...state, chk: { ...state.chk, [id]: value } });
}

export function setCursor(nodeId: string, index: number): void {
  if (state.cur[nodeId] === index) return;
  commit({ ...state, cur: { ...state.cur, [nodeId]: index } });
}

export function setReview(nodeId: string, row: RevRow): void {
  commit({ ...state, rev: { ...state.rev, [nodeId]: row } });
}

export function setGame(game: SavedGame | null): void {
  commit({ ...state, game });
}

export function resetAll(): void {
  commit({ done: {}, chk: {}, cur: {}, rev: {}, v: STATE_VERSION });
}

/* Load once at module init, then repair what the artifact's own migrations had
   to repair — a saved cursor from before blocks were inserted, and passes
   recorded before the review schedule existed. */
export function initProgress(): void {
  const blob = readBlob();
  state = blob ? blob.state : { ...EMPTY };
  migrateCursor();
  migrateReview();
  commit({ ...state });
}

/* Blocks were added to every chapter after the first release. A saved cursor
   pointed at the old indices, and a chapter finished before the new block
   existed must not silently re-lock the path. */
function migrateCursor(): void {
  if (state.v === STATE_VERSION) return;
  const cur: Record<string, number> = { ...state.cur };
  let changed = false;
  for (const [nodeId, index] of Object.entries(cur)) {
    if (typeof index === 'number' && index >= 3) {
      cur[nodeId] = index + 1;
      changed = true;
    }
  }
  if (changed) state = { ...state, cur };
}

/* Progress saved before the review schedule existed has passes but no
   intervals. Seed them as reviewed-at-load-time so those chapters enter the
   1-day interval instead of never coming back. */
function migrateReview(): void {
  const rev: Record<string, RevRow> = { ...state.rev };
  let changed = false;
  for (const [blockId, passed] of Object.entries(state.chk)) {
    if (!passed) continue;
    const nodeId = blockId.split('.')[0];
    if (!rev[nodeId]) {
      rev[nodeId] = { h: 1, n: 0, ts: Date.now() };
      changed = true;
    }
  }
  if (changed) state = { ...state, rev };
}

/* The language chosen on the original page, for a one-time migration into the
   detector's own storage key. Null once there is nothing to migrate. */
export function readLegacyLang(): Lang | null {
  return readBlob()?.lang ?? null;
}

/* Test seam: reset the module back to an empty, un-persisted state. */
export function __resetForTests(): void {
  state = { ...EMPTY };
  saveFailed = false;
  flushQueued = false;
  listeners = [];
  errorHandlers = [];
}
